import { Badge, Box, Button, Typography } from '@mui/material';
import classNames from 'classnames';
import { memo } from 'react';
import useTeceoStyles from '../../../../../../../../../../useTeceoStyles';
import { getColorByRole } from '../../../../../../../../../../utils/generics';
import { useApplicationContext } from '../../../../../../../../../context/ApplicationContext/ApplicationContext';
import { useTranslation } from '../../../../../../../../../translation/TranslationContext';
import { UserRole } from '../../../../../../../../../users/roles';
import { useChatContext } from '../../../../../../../../contexts/chat/chat.context';
import { ChatState } from '../../../../../../../../contexts/chat/hooks/useChats/reducer/chatListReducer.types';
import { formatChatTime } from '../../../../../../../../utils/chat.formatters';
import { invertChatRole, isPrivateChat } from '../../../../../../../../utils/chat.utils';
import ChatAvatar from '../../../../../../../ChatAvatar/ChatAvatar';
import { formatBrandStoreChat } from '../../../../../ChatContainerHeader/chatHeader.utils';
import { useChatRoomItemStyles } from './chatRoomItem.styles';
import ChatRoomItemMessage from './components/ChatRoomItemMessage/ChatRoomItemMessage';

type ChatRoomItemProps = {
  chat: ChatState;
};

const ChatRoomItem = ({ chat }: ChatRoomItemProps) => {
  'use no memo';

  const { classes: teceoClasses } = useTeceoStyles();
  const translation = useTranslation();

  const { isStore, userRole } = useApplicationContext();
  const { onChangeSelectedChat, getTotalUnreadMessageToChat } = useChatContext();

  const totalNotificationsToChat = getTotalUnreadMessageToChat(chat.id);

  const hasNotification = totalNotificationsToChat > 0;

  const { classes: styles } = useChatRoomItemStyles({
    hasNotification,
  });

  const { title, logoUrl } = formatBrandStoreChat({
    chat,
    isStore,
    translate: translation.translate,
  });

  const getLastMessage = () => {
    return chat.lastMessage
      ? {
          body: chat.lastMessage.body,
          type: chat.lastMessage.type,
          authorName: chat.lastMessage.authorName,
          authorEmail: chat.lastMessage.authorEmail,
        }
      : undefined;
  };

  return (
    <Button className={styles.button} onClick={() => onChangeSelectedChat(chat.id, true)}>
      <Box
        sx={{
          width: '100%',
          display: 'flex',
          gap: '12px',
        }}
      >
        <ChatAvatar
          size={4.5}
          src={logoUrl}
          chatType={chat.type}
          title={chat.title || isStore ? chat.brand.commercialName : chat.stores[0].commercialName}
          backgroundColor={getColorByRole({
            withSrc: !!logoUrl,
            transparentWhenHasSrc: false,
            userRole: invertChatRole(isPrivateChat(chat.type), isStore, userRole as UserRole),
          })}
        />

        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            flex: 1,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: '6px',
            }}
          >
            <Typography
              color="textPrimary"
              className={classNames(
                teceoClasses.truncate,
                teceoClasses.noTextTransform,
                styles.title,
              )}
            >
              {title}
            </Typography>

            <Box
              sx={{
                display: 'flex',
                gap: '12px',
                alignItems: 'baseline',
              }}
            >
              <ChatRoomItemMessage
                wasRemoved={chat.wasRemoved}
                lastMessage={getLastMessage()}
                weight={hasNotification ? 600 : 500}
              />

              <Typography noWrap variant="body2" color="textPrimary">
                {formatChatTime(chat.lastMessage?.updatedAt || chat.updatedAt, translation.locale)}
              </Typography>
            </Box>
          </Box>

          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <Badge
              classes={{ anchorOriginTopRightRectangular: styles.badge }}
              overlap="rectangular"
              badgeContent={totalNotificationsToChat}
              color="primary"
            />
          </Box>
        </Box>
      </Box>
    </Button>
  );
};

export default memo(ChatRoomItem);
